import GameObject, { GameObjectEvent } from '../../GameObject/GameObject';
import Element from '../UI/Element';
import { TextAlignment } from '../UI/TextAlignment';
import Text from '../Text';
import { applyDefaultButtonAnimation } from '../../System/UI/UIHelper';
import UITextComponent from '../../Component/UI/Text';
import Renderable from '../../Component/Renderable';

interface ILuckyTurnipsHUDParams {
    picksLeft: number;
    displayRestoreContextMessage: boolean;
}

export class LuckyTurnipsHUD extends Element {

    private static BUTTON_CLAIM_NAME = 'button_claim';
    private static BUTTON_CLAIM_CONTAINER_NAME = 'claim_button_container';
    private static PICKS_LEFT_TEXT_NAME = 'picks_left_number';

    private static textStyle = {
        font: 'FrancoisOne',
        fill: 'white',
        fontSize: 30,
        align: TextAlignment.CENTER,
        stroke: "#631c03",
        strokeThickness: 5,
        lineJoin: "round",
        miterLimit: 2,
    };

    private picksLeftText: Text;

    private buttonClaim: GameObject;
    private buttonClaimContainer: GameObject;

    public create(params: ILuckyTurnipsHUDParams) {
        super.create(this.createHUD(params));

        this.picksLeftText = this.findChildByName(
            LuckyTurnipsHUD.PICKS_LEFT_TEXT_NAME
        ) as unknown as Text;

        this.buttonClaim = this.findChildByName(
            LuckyTurnipsHUD.BUTTON_CLAIM_NAME
        );
        applyDefaultButtonAnimation(this.buttonClaim);

        this.buttonClaimContainer = this.findChildByName(
            LuckyTurnipsHUD.BUTTON_CLAIM_CONTAINER_NAME
        );
        this.buttonClaimContainer.getComponent(Renderable).visible = false;
    }

    // Public part

    public addClaimClickListener(listener: () => void) {
        this.buttonClaim.on(GameObjectEvent.SELECTED, listener);
    }

    public updatePicksLeft(picksLeft: number) {
        this.picksLeftText.getComponent(UITextComponent).text = String(picksLeft);
    }

    public setCollectAllVisibleTo(visible: boolean) {
        this.buttonClaimContainer.getComponent(Renderable).visible = visible;
    }

    //

    private createHUD(params: ILuckyTurnipsHUDParams) {
        const hudLayer = {
            name: 'layer_hud',
            children: [
                {
                    name: 'layer_hud_top',
                    children: [
                        this.createPicksLeftBoard(params.picksLeft),
                        this.createGameTitleBoard(),
                    ],
                },
                ...(
                    params.displayRestoreContextMessage ? [
                        this.createRestoreContextMessage()
                    ] : []
                ),
                this.createClaimButton(),
            ]
        };
        return hudLayer;
    }

    private createPicksLeftBoard(picksLeft: number) {
        return {
            name: 'picks_left',
            layout: {
                anchors: [ 0.5, 0, 0.5, 0 ],
                pivot: [ 0.5, 0 ],
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: 'luckyturnips_board_1',
                    },
                },
            ],
            children: [
                {
                    name: LuckyTurnipsHUD.PICKS_LEFT_TEXT_NAME,
                    layout: {
                        anchors: [ 0.5, 0, 0.5, 0 ],
                        offsets: [ 0, -60, 0, -60 ],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                text: String(picksLeft),
                                style: {
                                    ...LuckyTurnipsHUD.textStyle,
                                    fontSize: 70,
                                },
                            },
                        },
                    ],
                },
                {
                    name: 'picks_left_title',
                    layout: {
                        anchors: [ 0.5, 0, 0.5, 0 ],
                        offsets: [ 0, -128, 0, -128 ],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                textLocalizationKey: 'LuckyTurnipsPicksLeft',
                                style: {
                                    ...LuckyTurnipsHUD.textStyle,
                                    fill: '#f79b2f',
                                    stroke: '#7d351b',
                                },
                                bounds: [ 177, 80 ],
                            },
                        },
                    ],
                },
            ]
        };
    }

    private createGameTitleBoard() {
        return {
            name: 'game_title_board',
            layout: {
                anchors: [ 0.8, 0, 0.8, 0 ],
                pivot: [ 0.5, 0 ],
            },
            components: [
                {
                    type: 'image',
                    data: {
                        cacheKey: 'luckyturnips_board_2',
                    },
                },
            ],
            children: [
                {
                    name: 'game_title_head',
                    layout: {
                        anchors: [ 0.5, 0, 0.5, 0 ],
                        offsets: [ 0, -45, 0, -45 ],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                textLocalizationKey: 'LuckyTurnipsGameTitle',
                                style: {
                                    ...LuckyTurnipsHUD.textStyle,
                                    fontSize: 40,
                                },
                                bounds: [ 330, 80 ],
                            },
                        },
                    ],
                },
                {
                    name: 'game_title_description',
                    layout: {
                        anchors: [ 0.5, 0, 0.5, 0 ],
                        offsets: [ 0, -128, 0, -128 ],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                textLocalizationKey: 'LuckyTurnipsGameDescription',
                                style: {
                                    ...LuckyTurnipsHUD.textStyle,
                                    fontSize: 23,
                                },
                                bounds: [ 330, 80 ],
                            },
                        },
                    ],
                },
            ]
        }
    }

    private createRestoreContextMessage() {
        return {
            layout: {
                anchors: [ 0.5, 1, 0.5, 1 ],
                offsets: [ 0, 240, 0, 240 ],
            },
            children: [
                {
                    name: 'luckyturnips_note_bg',
                    layout: {
                        constrains: {
                            widthMax: 990,
                            heightMax: 150,
                        },
                    },
                    components: [
                        {
                            type: 'nineslice',
                            data: {
                                cacheKey: 'luckyturnips_notice_bg',
                                offset: [60, 30, 60, 30]
                            }
                        }
                    ]
                },
                {
                    layout: {
                        offsets: [0, 5, 0, 5],
                    },
                    components: [
                        {
                            type: 'text',
                            data: {
                                textLocalizationKey: 'LuckyTurnipsRestoreSessionMessage',
                                style: {
                                    fontSize: 30,
                                    font: 'FrancoisOne',
                                    fill: 'white',
                                },
                                bounds: [ 970, 150 ],
                                align: 'center',
                            },
                        },
                    ]
                }
            ],
        };
    }

    private createClaimButton() {
        return {
            name: LuckyTurnipsHUD.BUTTON_CLAIM_CONTAINER_NAME,
            layout: {
                anchors: [ 0.5, 1, 0.5, 1 ],
                offsets: [ 0, 90, 0, 90 ],
            },
            children: [
                {
                    name: LuckyTurnipsHUD.BUTTON_CLAIM_NAME,
                    components: [
                        {
                            type: 'input',
                            data: {
                                enabled: true,
                                buttonMode: true,
                            },
                        },
                        {
                            type: 'image',
                            data: {
                                cacheKey: 'luckyturnips_button_front',
                            },
                        },
                    ],
                    children: [
                        {
                            name: 'claim_button_text',
                            layout: {
                                offsets: [ 0, 8, 0, 8 ],
                            },
                            components: [
                                {
                                    type: 'text',
                                    data: {
                                        textLocalizationKey: 'LuckyTurnipsCollectAll',
                                        style: {
                                            ...LuckyTurnipsHUD.textStyle,
                                            fontSize: 40,
                                            stroke: '#a11d19',
                                        },
                                        bounds: [ 208, 80 ],
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        };
    }
}
